import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Send, Upload, FileText, Image as ImageIcon, Film, Music,
  Calendar, User, CheckCircle2, X, AlertCircle, Mic,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";
import VoicePlayer from "@/components/VoicePlayer";

const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  acknowledged: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-cyan-50 text-cyan-700 border-cyan-200",
  extended: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  task_over: "bg-slate-100 text-slate-700 border-slate-200",
};
const PRIORITY_TONE = {
  urgent: "text-red-700 bg-red-100",
  high: "text-orange-700 bg-orange-100",
  medium: "text-cyan-700 bg-cyan-100",
  low: "text-slate-600 bg-slate-100",
};

function fileIconFor(name = "") {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "heic", "gif"].includes(ext)) return ImageIcon;
  if (["mp4", "mov", "webm", "avi"].includes(ext)) return Film;
  if (["mp3", "m4a", "wav", "ogg", "aac"].includes(ext)) return Music;
  return FileText;
}

function fmtDateTime(s) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function DelegationDetail() {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [replyAtt, setReplyAtt] = useState([]);
  const [replyVoice, setReplyVoice] = useState(null);
  const [replyVoiceDur, setReplyVoiceDur] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [newStatus, setNewStatus] = useState("");
  const [estimatedDate, setEstimatedDate] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/delegation/tasks/${id}`);
      setTask(data);
      setProgress(data.progress_percentage || 0);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { /* ignore */ }
    })();
    refresh();
  }, [refresh]);

  const isAssignee = me?.id === task?.assigned_to;
  const isAssigner = me?.id === task?.assigned_by || me?.id === task?.on_behalf_of;

  const onUpload = async (fileList) => {
    if (!fileList?.length) return;
    for (const f of fileList) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 25 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("task_id", id);
      try {
        const { data } = await api.post("/delegation/attachments", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setReplyAtt((p) => [...p, data]);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  const sendMessage = async () => {
    if (!reply.trim() && replyAtt.length === 0 && !replyVoice && !newStatus) {
      toast.error("Add text, attachments, voice note, or change status");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/delegation/tasks/${id}/messages`, {
        text: reply.trim() || null,
        attachment_ids: replyAtt.map((a) => a.id),
        voice_url: replyVoice || null,
        voice_duration: replyVoice ? replyVoiceDur : null,
        new_status: newStatus || null,
        progress_percentage: progress !== (task.progress_percentage || 0) ? progress : null,
      });
      toast.success("Message sent");
      setReply("");
      setReplyAtt([]);
      setReplyVoice(null);
      setReplyVoiceDur(0);
      setNewStatus("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const quickStatus = async (s, opts = {}) => {
    setSubmitting(true);
    try {
      await api.post(`/delegation/tasks/${id}/status`, {
        status: s,
        reason: opts.reason || null,
        estimated_completion_date: opts.estimatedDate || null,
        progress_percentage: opts.progress ?? null,
      });
      toast.success(`Marked as ${s.replace("_", " ")}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>;
  if (!task) return <div className="text-sm text-slate-500 py-8 text-center">Task not found</div>;

  return (
    <div className="space-y-5" data-testid="delegation-detail">
      <Link to="/delegation/tasks" className="text-xs brand-text inline-flex items-center gap-1 hover:underline">
        <ArrowLeft className="h-3 w-3" /> Back to tasks
      </Link>

      {/* Header */}
      <div className="bg-white border border-red-100 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
              {task.task_number}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Chivo" }}>
              {task.title}
            </h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${PRIORITY_TONE[task.priority]}`}>
                {task.priority}
              </span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${STATUS_TONE[task.status]}`}
                    data-testid="task-status-pill">
                {task.status.replace("_", " ")}
              </span>
              {task.category && (
                <span className="text-[11px] text-slate-500">{task.category}</span>
              )}
            </div>
          </div>
          <div className="space-y-1 text-xs">
            <div className="text-slate-500 flex items-center gap-1">
              <User className="h-3 w-3" /> From: <b className="text-slate-900">{task.assigned_by_name}</b>
              {task.assigned_by_role && <span className="text-slate-400">({task.assigned_by_role})</span>}
            </div>
            {task.on_behalf_of_name && (
              <div className="text-slate-500 ml-4">on behalf of <b className="text-slate-900">{task.on_behalf_of_name}</b></div>
            )}
            <div className="text-slate-500 flex items-center gap-1">
              <User className="h-3 w-3" /> To: <b className="text-slate-900">{task.assigned_to_name}</b>
            </div>
            <div className="text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Deadline: <b className="text-slate-900">{task.target_finish_date}</b>
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
            <span>Progress</span>
            <span className="font-medium brand-text">{task.progress_percentage || 0}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full brand-bg transition-all" style={{ width: `${task.progress_percentage || 0}%` }} />
          </div>
        </div>
      </div>

      {/* Quick actions for assignee */}
      {isAssignee && task.status === "pending" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="acknowledge-strip">
          <div className="text-sm text-amber-900">
            <AlertCircle className="h-4 w-4 inline mr-1.5" />
            Please acknowledge this task to confirm receipt.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="date"
              value={estimatedDate}
              onChange={(e) => setEstimatedDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              max={task.target_finish_date}
              className="h-8 text-xs w-[150px]"
              data-testid="estimated-date"
            />
            <Button
              size="sm"
              onClick={() => quickStatus("acknowledged", { estimatedDate })}
              disabled={submitting}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="acknowledge-btn"
            >
              Acknowledge & Start
            </Button>
          </div>
        </div>
      )}

      {/* Communication thread */}
      <div className="space-y-3" data-testid="thread">
        {(task.communications || []).map((c) => {
          const fromAssigner = c.direction === "from_assigner";
          return (
            <div key={c.id} className={`flex ${fromAssigner ? "justify-start" : "justify-end"}`}
                 data-testid={`comm-${c.id}`}>
              <div className={`max-w-[80%] rounded-xl border p-3 ${
                fromAssigner ? "bg-white border-slate-200" : "bg-red-50/50 border-red-200"
              }`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-[11px] font-semibold text-slate-900">
                    {c.sender_name}
                    <span className="ml-1 text-slate-400 font-normal">({c.sender_role})</span>
                  </div>
                  <div className="text-[10px] text-slate-400">{fmtDateTime(c.created_at)}</div>
                </div>
                {c.text_content && (
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.text_content}</div>
                )}
                {c.voice_url && (
                  <div className="mt-2 flex items-center gap-2 bg-red-50/40 border border-red-200 rounded px-2 py-1.5">
                    <Mic className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />
                    <VoicePlayer
                      src={c.voice_url}
                      className="h-7 max-w-[260px]"
                      data-testid={`comm-voice-${c.id}`}
                    />
                    {c.voice_duration ? (
                      <span className="text-[10px] text-slate-500">
                        {String(Math.floor(c.voice_duration / 60)).padStart(2, "0")}:
                        {String(c.voice_duration % 60).padStart(2, "0")}
                      </span>
                    ) : null}
                  </div>
                )}
                {c.previous_status && c.new_status && c.previous_status !== c.new_status && (
                  <div className="text-[10px] mt-1.5 inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded">
                    Status: <b>{c.previous_status}</b> → <b>{c.new_status}</b>
                  </div>
                )}
                {c.attachments && c.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {c.attachments.map((a) => {
                      const Icon = fileIconFor(a.file_name);
                      return (
                        <a
                          key={a.id}
                          href={a.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs hover:bg-slate-50"
                          data-testid={`att-${a.id}`}
                        >
                          <Icon className="h-3.5 w-3.5 text-slate-500" />
                          <span className="flex-1 truncate">{a.file_name}</span>
                          <span className="text-[10px] text-slate-400">{((a.file_size || 0) / 1024 / 1024).toFixed(2)} MB</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply composer */}
      {task.status !== "task_over" && task.status !== "cancelled" && (
        <div className="bg-white border border-red-100 rounded-xl p-4 space-y-3" data-testid="reply-composer">
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isAssignee ? "Update progress, ask questions, share status..." : "Send guidance or feedback..."}
            data-testid="reply-text"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
              <Upload className="h-3.5 w-3.5" />
              <span>Attach files</span>
              <input type="file" multiple onChange={(e) => onUpload(e.target.files)} className="hidden"
                     data-testid="reply-attach-input" />
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {isAssignee && task.status !== "pending" && task.status !== "completed" && (
                <>
                  <Input
                    type="number" min="0" max="100"
                    value={progress}
                    onChange={(e) => setProgress(Math.max(0, Math.min(100, Number(e.target.value))))}
                    className="h-8 w-[80px] text-xs"
                    data-testid="progress-input"
                  />
                  <span className="text-[10px] text-slate-500">%</span>
                </>
              )}
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="status-select">
                  <SelectValue placeholder="Change status..." />
                </SelectTrigger>
                <SelectContent>
                  {isAssignee && task.status !== "in_progress" && <SelectItem value="in_progress">In progress</SelectItem>}
                  {isAssignee && <SelectItem value="extended">Request extension</SelectItem>}
                  {isAssignee && task.status !== "completed" && <SelectItem value="completed">Mark complete</SelectItem>}
                  {isAssigner && task.status === "completed" && <SelectItem value="task_over">Close task</SelectItem>}
                  {isAssigner && <SelectItem value="cancelled">Cancel</SelectItem>}
                </SelectContent>
              </Select>
              <Button
                onClick={sendMessage}
                disabled={submitting || (!reply.trim() && replyAtt.length === 0 && !replyVoice && !newStatus)}
                className="brand-bg brand-bg-hover text-white h-8 text-xs"
                data-testid="send-message-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" /> {submitting ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
          {/* Voice recorder for reply */}
          <div>
            <VoiceRecorder
              value={replyVoice}
              onChange={(url, dur) => { setReplyVoice(url); setReplyVoiceDur(dur || 0); }}
              uploadEndpoint="/delegation/attachments"
              uploadFields={{ task_id: id }}
              urlFromResponse={(d) => d.file_url}
              testIdPrefix="reply-voice"
              compact
            />
          </div>
          {replyAtt.length > 0 && (
            <div className="space-y-1" data-testid="reply-attachments">
              {replyAtt.map((a) => {
                const Icon = fileIconFor(a.file_name);
                return (
                  <div key={a.id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs">
                    <Icon className="h-3.5 w-3.5 text-slate-500" />
                    <span className="flex-1 truncate">{a.file_name}</span>
                    <button onClick={() => setReplyAtt((p) => p.filter((x) => x.id !== a.id))}
                            className="text-red-600 hover:underline">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Final action card */}
      {task.status === "completed" && isAssigner && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="review-strip">
          <div className="text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 inline mr-1.5" />
            Task completed. Review and close it when satisfied.
          </div>
          <Button
            size="sm"
            onClick={() => quickStatus("task_over")}
            disabled={submitting}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="close-task-btn"
          >
            Close (Task Over)
          </Button>
        </div>
      )}
    </div>
  );
}
